/* Device-only household and supplies data. Addresses and risk reports are never stored. */
(function (scope) {
  'use strict';
  const STORAGE_KEY = 'sonamaru-preparedness-v1';
  const CATEGORIES = Object.freeze(['water', 'toilet', 'food', 'pet', 'other']);

  function emptyState() { return { version: 1, household: null, items: [] }; }

  function dateDay(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new RangeError('INVALID_DATE');
    const [year, month, day] = value.split('-').map(Number);
    if (year < 1900 || year > 2200) throw new RangeError('INVALID_DATE');
    const date = new Date(Date.UTC(year, month - 1, day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) throw new RangeError('INVALID_DATE');
    return date.getTime() / 86400000;
  }

  function todayString(date = new Date()) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  function normalizeHousehold(value) {
    if (value === null) return null;
    if (!value || !Number.isInteger(value.people) || value.people < 1 || value.people > 30 || ![3, 7].includes(value.days)) throw new RangeError('INVALID_HOUSEHOLD');
    return { people: value.people, days: value.days, children: value.children === true, seniors: value.seniors === true, pets: value.pets === true };
  }

  function normalizeItem(value) {
    if (!value || typeof value.id !== 'string' || !/^[a-zA-Z0-9-]{1,80}$/.test(value.id) || typeof value.name !== 'string' || !value.name.trim() || value.name.trim().length > 120 || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value.name) || /[\ud800-\udfff]/u.test(value.name) || !CATEGORIES.includes(value.category)) throw new RangeError('INVALID_ITEM');
    if (!['string', 'number'].includes(typeof value.amount) || (typeof value.amount === 'string' && !value.amount.trim())) throw new RangeError('INVALID_AMOUNT');
    const amount = Number(value.amount);
    if (!Number.isFinite(amount) || amount <= 0 || amount > 1000000 || Math.abs(Math.round(amount * 1000) - amount * 1000) > 0.000001 || (value.category !== 'water' && !Number.isInteger(amount))) throw new RangeError('INVALID_AMOUNT');
    if (typeof value.expiry !== 'string') throw new RangeError('INVALID_DATE');
    if (value.expiry) dateDay(value.expiry);
    return { id: value.id, name: value.name.trim(), category: value.category, amount: Math.round(amount * 1000) / 1000, expiry: value.expiry };
  }

  function normalizeState(value) {
    if (!value || value.version !== 1 || !Array.isArray(value.items) || value.items.length > 200) throw new RangeError('INVALID_DATA');
    const items = value.items.map(normalizeItem);
    if (new Set(items.map(item => item.id)).size !== items.length) throw new RangeError('DUPLICATE_ITEM');
    return { version: 1, household: normalizeHousehold(value.household), items };
  }

  function mergeBackup(current, incoming) {
    const state = normalizeState(current);
    const backup = normalizeState(incoming);
    const existing = new Set(state.items.map(item => item.id));
    const added = backup.items.filter(item => !existing.has(item.id));
    if (state.items.length + added.length > 200) throw new RangeError('ITEM_LIMIT');
    return { state: { ...state, items: [...state.items, ...added] }, added: added.length, skipped: backup.items.length - added.length };
  }

  function read(storage) {
    const raw = storage.getItem(STORAGE_KEY);
    return raw === null ? emptyState() : normalizeState(JSON.parse(raw));
  }

  function write(storage, value) {
    const safe = normalizeState(value);
    storage.setItem(STORAGE_KEY, JSON.stringify(safe));
    return safe;
  }

  function expiryStatus(item, today = todayString()) {
    if (!item.expiry) return 'unknown';
    const days = dateDay(item.expiry) - dateDay(today);
    return days < 0 ? 'expired' : days <= 30 ? 'soon' : 'ready';
  }

  function summarize(items, today = todayString()) {
    const summary = { waterLitres: 0, toiletUses: 0, expired: 0, unknown: 0, soon: 0 };
    let waterMl = 0;
    for (const value of items) {
      const item = normalizeItem(value);
      const status = expiryStatus(item, today);
      if (status === 'unknown' || status === 'expired') { summary[status] += 1; continue; }
      if (status === 'soon') summary.soon += 1;
      if (item.category === 'water') waterMl += Math.round(item.amount * 1000);
      if (item.category === 'toilet') summary.toiletUses += item.amount;
    }
    summary.waterLitres = waterMl / 1000;
    return summary;
  }

  function deficits(amounts, stock) {
    return { waterLitres: Math.max(0, Math.round((amounts.waterLitres - stock.waterLitres) * 1000) / 1000), toiletUses: Math.max(0, amounts.toiletUses - stock.toiletUses) };
  }

  function messages(root) {
    try { return JSON.parse(root.closest('.sm-place')?.querySelector('[data-sm-messages]')?.textContent || '{}'); }
    catch (_error) { return {}; }
  }

  function text(dictionary, key, values = {}) {
    return (dictionary[key] || key).replace(/__([a-z]+)__|\{\{\s*(\w+)\s*\}\}/g, (_match, marker, token) => String(values[marker || token] ?? ''));
  }

  scope.SonamaruPreparedness = Object.freeze({ STORAGE_KEY, CATEGORIES, emptyState, dateDay, todayString, normalizeHousehold, normalizeItem, normalizeState, mergeBackup, read, write, expiryStatus, summarize, deficits, messages, text });
  if (typeof module !== 'undefined' && module.exports) module.exports = scope.SonamaruPreparedness;
})(typeof globalThis !== 'undefined' ? globalThis : window);
